import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import MetaTags from "../../components/SEO/MetaTags";
import { pageConfigs } from "../../utils/seo";

export default function SessionWroteNote() {
  return (
    <>
      <MetaTags
        title={pageConfigs.blogSessionWroteNote.title}
        description={pageConfigs.blogSessionWroteNote.description}
        keywords={pageConfigs.blogSessionWroteNote.keywords}
        canonical={pageConfigs.blogSessionWroteNote.canonical}
      />
      <div className="bg-white">
      {/* Hero section with title */}
      <div className="bg-darkGreen text-white pt-36 pb-8 mb-16 relative overflow-hidden">
        <div className="relative z-10">
          <div className="max-w-3xl mx-auto text-center px-4">
            <h4 className="text-emerald-400 font-medium mb-2">Blog Post</h4>
            <h1 className="text-4xl lg:text-5xl font-bold mb-4 font-productSans">
              What If the Session Wrote the Note?
            </h1>
            <p className="text-emerald-100 text-lg">
              Rethinking Documentation Timing in Physical Therapy
            </p>
            <p className="text-emerald-200 text-sm mt-4">
              By Christina Rama PT, DPT • July 18, 2025
            </p>
          </div>
        </div>
      </div>

      {/* Blog content */}
      <div className="max-w-3xl mx-auto px-4 pb-16">
        {/* Back button at top */}
        <div className="mb-8">
          <Link
            to="/blogs"
            className="inline-flex items-center px-6 py-3 border border-gray-800 rounded-full text-gray-800 hover:bg-darkGreen hover:text-white transition-colors"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Blogs
          </Link>
        </div>

        {/* Blog image */}
        <div className="mb-12">
          <img
            src="/blog/blog-4.png"
            alt="What If the Session Wrote the Note?"
            className="w-full rounded-lg shadow-lg"
          />
        </div>

        {/* Article content */}
        <article className="prose prose-lg max-w-none">
          <p className="text-gray-700 leading-relaxed mb-6">
            PT documentation isn't just a time issue—it's a timing issue.
          </p>
          
          <p className="text-gray-700 leading-relaxed mb-6">
            It shows up between sessions, pulls you out of the moment, or lingers at the end of the day when your energy is gone.
          </p>

          <p className="text-gray-700 leading-relaxed mb-6">
            Most of us can get the notes done. That's not the issue.
            The issue is how often it interrupts the rhythm of care, the end of the day, or the space to just think.
          </p>

          <p className="text-gray-700 leading-relaxed mb-8">
            And it's not because we're falling behind—it's because the systems around us weren't built for how PTs actually work: 
            dynamic sessions, rapid reasoning, hands-on interaction, and the need to stay focused on people—not keyboards.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">When Documentation Breaks Your Flow</h2>

          <p className="text-gray-700 leading-relaxed mb-6">
            Documentation isn't the enemy.
            Good notes protect patients, reflect our thinking, support outcomes, and ensure we get paid.
          </p>

          <p className="text-gray-700 leading-relaxed mb-6">
            But when it pulls us away from the clinical moment—or lingers long after it—it adds friction to the work we love.
            Even subtle interruptions can take a toll—lost momentum, forgotten details, mental fatigue.
          </p>

          <p className="text-gray-700 leading-relaxed mb-8">
            PTs deserve time that works with them, not against them.
            Time to focus on care. Time to think clearly. Time to leave when the work is done.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">What Could It Look Like Instead?</h2>

          <p className="text-gray-700 leading-relaxed mb-6">
            What if documentation didn't compete with care—but followed it?
            What if your reasoning, your interaction, and your observations could be reflected while you worked, not after?
          </p>

          <p className="text-gray-700 leading-relaxed mb-6">
            That's what we've built at Notation: a way to generate notes in real time, alongside the session, without adding burden.
          </p>

          <p className="text-gray-700 leading-relaxed mb-2">So you can:</p>
          <ul className="list-disc list-inside text-gray-700 leading-relaxed mb-8 ml-4">
            <li>Focus on care</li>
            <li>Chart less</li>
            <li>End your day on time</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">Where Notation Fits In</h2>

          <p className="text-gray-700 leading-relaxed mb-6">
            At Notation, we've built a way to document during the session—while care is actually happening.
            It's designed to follow your clinical flow, not interrupt it.
          </p>

          <p className="text-gray-700 leading-relaxed mb-6">
            The goal isn't just to finish notes faster. It's to capture meaningful details in the moment, when they're most accurate—so your documentation reflects your thinking without pulling you out of the session.
          </p>

          <p className="text-gray-700 leading-relaxed mb-8">
            Because when notes fit into care, they stop getting in the way—and start working for you.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">Try Notation in Your Next Evaluation</h2>

          <p className="text-gray-700 leading-relaxed mb-6">
            If you've ever wanted your notes to come together during the session—not after it—Notation was built for you.
            It captures what matters while you treat, so you can stay focused and finish on time.
          </p>

          <div className="bg-gradient-to-r from-[#619ed3] to-[#5c91a4] rounded-2xl p-8 text-center my-12">
            <p className="text-white text-lg font-semibold mb-6">
              👉 Try Notation for your next evaluation — no strings attached.
            </p>
            <a
              href="https://notation.fownd.care/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-white text-[#619ed3] px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors shadow-lg"
            >
              Try Notation
            </a>
          </div>
        </article>

        {/* Back button at bottom */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <Link
            to="/blogs"
            className="inline-flex items-center px-6 py-3 border border-gray-800 rounded-full text-gray-800 hover:bg-darkGreen hover:text-white transition-colors"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Blogs
          </Link>
        </div>
      </div>
      </div>
    </>
  );
} 